import { get } from 'aws-amplify/api';
import { logger } from 'src/logger';

/** BRASS group types supported by the DaSFinance BRASS API */
export enum BrassGroupType {
  AntGroup = 'antGroup',
  PosixGroup = 'posixGroup',
  LdapGroup = 'ldapGroup'
}

interface BrassGroupMembershipResponse {
  statusCode: number;
  details: {
    membershipList: Array<{ group_id: string; group_type: string }>;
    next_page?: string | null;
  };
}

/**
 * Service for interacting with the DaSFinance BRASS API.
 * Provides group membership queries via the /membership endpoints.
 */
export class BrassService {
  /**
   * Lists all groups of a given type that a user is a member of.
   * Fails open: returns empty array on error.
   *
   * @param username - The user's login alias
   * @param groupType - The BRASS group type
   * @returns Array of group IDs the user belongs to
   */
  static async listActorGroupsForMember(username: string, groupType: BrassGroupType): Promise<string[]> {
    try {
      const allGroupIds: string[] = [];
      let marker: string | undefined;

      do {
        const queryParams: Record<string, string> = { username, groupType };
        if (marker) queryParams.marker = marker;

        const response = await get({
          apiName: 'BRASS_API',
          path: '/membership/list_actor_groups_for_member',
          options: { queryParams }
        }).response;

        const body = (await response.body.json()) as unknown as BrassGroupMembershipResponse;
        if (body.details?.membershipList?.length) {
          allGroupIds.push(...body.details.membershipList.map((g) => g.group_id));
        }
        marker = body.details?.next_page ?? undefined;
      } while (marker);

      return allGroupIds;
    } catch (error) {
      logger.error('BrassService.listActorGroupsForMember failed', { error, username, groupType });
      return [];
    }
  }

  /**
   * Checks if a user is a member of a specific group.
   * Fails open: returns false on error.
   *
   * @param username - The user's login alias
   * @param groupId - The group identifier (e.g., 'panos-ea')
   * @param groupType - The BRASS group type
   * @returns true if the user is a member of the group
   */
  static async isMemberOf(username: string, groupId: string, groupType: BrassGroupType): Promise<boolean> {
    try {
      const response = await get({
        apiName: 'BRASS_API',
        path: '/membership/is_member_of',
        options: { queryParams: { username, groupId, groupType } }
      }).response;

      const body = (await response.body.json()) as unknown as { statusCode: number; details: boolean };
      return body.details === true;
    } catch (error) {
      logger.error('BrassService.isMemberOf failed', { error, username, groupId, groupType });
      return false;
    }
  }

  /**
   * Lists all members of a given group.
   * Fails open: returns empty array on error.
   *
   * @param groupId - The group identifier (e.g., 'nik-team')
   * @param groupType - The BRASS group type
   * @returns Array of member login aliases
   */
  static async listActorGroupMembership(groupId: string, groupType: BrassGroupType): Promise<string[]> {
    try {
      const allMembers: string[] = [];
      let marker: string | undefined;

      do {
        const queryParams: Record<string, string> = { groupId, groupType };
        if (marker) queryParams.marker = marker;

        const response = await get({
          apiName: 'BRASS_API',
          path: '/membership/list_actor_group_membership',
          options: { queryParams }
        }).response;

        const body = (await response.body.json()) as unknown as { statusCode: number; details: { members: string[]; next_page?: string | null } };
        if (body.details?.members?.length) {
          allMembers.push(...body.details.members);
        }
        marker = body.details?.next_page ?? undefined;
      } while (marker);

      return allMembers;
    } catch (error) {
      logger.error('BrassService.listActorGroupMembership failed', { error, groupId, groupType });
      return [];
    }
  }
}
